import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import {
  Link2, Plus, Loader2, Copy, Trash2, CheckCircle2, Clock, Mail,
  Activity, Eye, ShieldCheck, ChevronDown, ChevronUp, Users, MessageSquare, Send,
  Edit3, Check, X, AlertTriangle
} from 'lucide-react';

const TRADES = [
  'Mechanical', 'Electrical', 'Plumbing', 'Fire Protection',
  'Architectural', 'Structural', 'Civil', 'Landscape',
  'HVAC', 'Controls', 'Roofing', 'Glazing', 'Painting',
];

export default function TradePortalManager({ projectId }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [trade, setTrade] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showMessages, setShowMessages] = useState(false);
  const [messageThreads, setMessageThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [showProposals, setShowProposals] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [pmNotes, setPmNotes] = useState('');

  const fetchLinks = useCallback(async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/trade-portal/links`);
      setLinks(res.data.links || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const res = await api.get(`/closeout/projects/${projectId}/trade-portal/activity`);
      setActivityData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setActivityLoading(false);
    }
  }, [projectId]);

  const fetchMessageThreads = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const res = await api.get(`/closeout/projects/${projectId}/message-threads`);
      setMessageThreads(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setMessagesLoading(false);
    }
  }, [projectId]);

  const fetchThreadMessages = useCallback(async (threadType, threadId) => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/messages?thread_type=${threadType}`);
      const filtered = (res.data || []).filter(m => m.thread_id === threadId);
      setThreadMessages(filtered.sort((a, b) => a.created_at.localeCompare(b.created_at)));
    } catch (err) {
      console.error(err);
    }
  }, [projectId]);

  const handleReply = async (msgId) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await api.post(`/closeout/projects/${projectId}/portal-messages/${msgId}/reply`, { text: replyText });
      setReplyText('');
      if (activeThread) fetchThreadMessages(activeThread.thread_type, activeThread.thread_id);
      fetchMessageThreads();
      const { toast } = await import('sonner');
      toast.success('Reply sent');
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const fetchProposals = useCallback(async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/portal-proposals`);
      setProposals(res.data || []);
    } catch (err) { console.error(err); }
  }, [projectId]);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  const handleReviewProposal = async (proposalId, action) => {
    setReviewingId(proposalId);
    try {
      await api.post(`/closeout/projects/${projectId}/portal-proposals/${proposalId}/review`, {
        action,
        pm_notes: pmNotes,
      });
      const { toast } = await import('sonner');
      toast.success(`Proposal ${action}ed`);
      setPmNotes('');
      fetchProposals();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setReviewingId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email || !trade) return;
    setCreating(true);
    try {
      await api.post(`/closeout/projects/${projectId}/trade-portal/links`, { email, trade });
      const { toast } = await import('sonner');
      toast.success(`Trade portal link created for ${trade}`);
      setEmail('');
      setTrade('');
      setShowForm(false);
      fetchLinks();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleBulkInvite = async () => {
    const lines = bulkText.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return;
    const pairs = [];
    const errors = [];
    for (let i = 0; i < lines.length; i++) {
      const parts = lines[i].split(/[,\t]+/).map(s => s.trim()).filter(Boolean);
      if (parts.length < 2) {
        errors.push(`Line ${i + 1}: Expected "email, trade" — got "${lines[i].trim()}"`);
        continue;
      }
      const em = parts[0];
      const tr = parts[1];
      if (!em.includes('@')) {
        errors.push(`Line ${i + 1}: "${em}" doesn't look like an email`);
        continue;
      }
      pairs.push({ email: em, trade: tr });
    }
    if (errors.length > 0 && pairs.length === 0) {
      setBulkResult({ success: 0, failed: errors.length, errors });
      return;
    }
    setBulkCreating(true);
    setBulkResult(null);
    let success = 0;
    const failErrors = [...errors];
    for (const pair of pairs) {
      try {
        await api.post(`/closeout/projects/${projectId}/trade-portal/links`, pair);
        success++;
      } catch (err) {
        failErrors.push(`${pair.email} (${pair.trade}): ${err.response?.data?.detail || 'failed'}`);
      }
    }
    setBulkResult({ success, failed: failErrors.length, errors: failErrors });
    setBulkCreating(false);
    if (success > 0) {
      fetchLinks();
      const { toast } = await import('sonner');
      toast.success(`Created ${success} portal link${success !== 1 ? 's' : ''}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="trade-portal-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Trade Portal Links
          </h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Generate scoped links for subcontractors. They verify their email before accessing their trade's data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowBulk(!showBulk); setShowForm(false); }} data-testid="bulk-invite-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider text-muted-foreground border border-border rounded-sm hover:bg-muted/20 transition-colors">
            <Users className="w-3.5 h-3.5" /> Bulk Invite
          </button>
          <button onClick={() => { setShowForm(!showForm); setShowBulk(false); }} data-testid="create-trade-link-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/20 transition-colors">
            <Plus className="w-3.5 h-3.5" /> New Link
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-4 p-4 bg-card border border-primary/20 rounded-sm space-y-3" data-testid="create-trade-link-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Sub Email</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="sub@company.com" data-testid="trade-link-email"
                className="mt-1 w-full px-3 py-1.5 text-xs bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Trade</label>
              <select required value={trade} onChange={e => setTrade(e.target.value)} data-testid="trade-link-trade"
                className="mt-1 w-full px-3 py-1.5 text-xs bg-secondary border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/50">
                <option value="">Select trade...</option>
                {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={creating} data-testid="submit-trade-link-btn"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Generate Link
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-2 text-xs text-muted-foreground">Cancel</button>
          </div>
        </form>
      )}

      {showBulk && (
        <div className="mb-4 p-4 bg-card border border-border rounded-sm space-y-3" data-testid="bulk-invite-form">
          <div>
            <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              Paste emails and trades (one per line)
            </label>
            <p className="text-[9px] text-muted-foreground mt-0.5 mb-1.5">
              Format: email, trade — e.g. <span className="font-mono">plumber@co.com, Plumbing</span>
            </p>
            <textarea
              value={bulkText}
              onChange={e => { setBulkText(e.target.value); setBulkResult(null); }}
              rows={5}
              placeholder={"sub1@company.com, Mechanical\nsub2@company.com, Electrical\nsub3@company.com, Plumbing"}
              data-testid="bulk-invite-textarea"
              className="w-full px-3 py-2 text-xs font-mono bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-primary/50 resize-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleBulkInvite} disabled={bulkCreating || !bulkText.trim()} data-testid="submit-bulk-invite-btn"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50">
              {bulkCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
              {bulkCreating ? 'Creating...' : 'Send All Invites'}
            </button>
            <button onClick={() => { setShowBulk(false); setBulkResult(null); setBulkText(''); }}
              className="px-3 py-2 text-xs text-muted-foreground">Cancel</button>
            {bulkResult && (
              <span className="text-[10px] font-mono ml-2">
                <span className="text-emerald-600 dark:text-emerald-400">{bulkResult.success} created</span>
                {bulkResult.failed > 0 && <span className="text-rose-600 dark:text-rose-400 ml-2">{bulkResult.failed} failed</span>}
              </span>
            )}
          </div>
          {bulkResult?.errors?.length > 0 && (
            <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/40 rounded-sm p-2 text-[10px] font-mono text-rose-600 dark:text-rose-400 space-y-0.5 max-h-24 overflow-y-auto"
              data-testid="bulk-invite-errors">
              {bulkResult.errors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )}
        </div>
      )}

      {links.length > 0 ? (
        <div className="space-y-2">
          {links.map(link => (
            <div key={link.id} className="flex items-center gap-3 px-4 py-3 border border-border rounded-sm bg-card" data-testid={`trade-link-${link.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{link.trade}</span>
                  {link.verified ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-700 bg-emerald-100 border border-emerald-300 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800 px-1.5 py-0.5 rounded-sm">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-700 bg-amber-100 border border-amber-300 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800 px-1.5 py-0.5 rounded-sm">
                      <Clock className="w-3 h-3" /> Pending
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <Mail className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">{link.invited_email}</span>
                  <span className="text-[10px] text-muted-foreground">Expires: {new Date(link.expires_at).toLocaleDateString()}</span>
                </div>
              </div>
              <button onClick={() => handleCopy(link.portal_url)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-mono text-muted-foreground border border-border rounded-sm hover:bg-muted/30 transition-colors"
                data-testid={`copy-link-${link.id}`}>
                {copied === link.portal_url ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                {copied === link.portal_url ? 'Copied' : 'Copy Link'}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-32 bg-card border border-dashed border-border rounded-sm" data-testid="no-trade-links">
          <Link2 className="w-8 h-8 text-muted-foreground/40 mb-2" />
          <p className="text-xs text-muted-foreground mb-1">No trade portal links yet</p>
          <p className="text-[10px] text-muted-foreground/60">Create links to give subcontractors scoped access to their trade's data</p>
        </div>
      )}

      {/* ── Proposals Section ─────────────────────────────────────── */}
      <div className="mt-6 border-t border-border pt-4" data-testid="portal-proposals-section">
        <button
          onClick={() => setShowProposals(!showProposals)}
          className="flex items-center gap-2 w-full text-left group"
          data-testid="toggle-proposals-btn"
        >
          <Edit3 className="w-4 h-4 text-amber-500" />
          <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80">Sub Attribute Proposals</h3>
          {proposals.filter(p => p.status === 'pending').length > 0 && (
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 text-[9px] font-mono font-bold text-white">
              {proposals.filter(p => p.status === 'pending').length}
            </span>
          )}
          {showProposals ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto" />}
        </button>
        <p className="text-[10px] text-muted-foreground mt-0.5 ml-6">
          Review attribute updates proposed by subcontractors. Approved changes are written to the asset.
        </p>

        {showProposals && (
          <div className="mt-3 space-y-2">
            {proposals.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-20 bg-card border border-dashed border-border rounded-sm">
                <Edit3 className="w-5 h-5 text-muted-foreground/40 mb-1" />
                <p className="text-[10px] text-muted-foreground">No proposals from subs yet</p>
              </div>
            ) : (
              proposals.map(p => {
                const isPending = p.status === 'pending';
                return (
                  <div key={p.id} className={`border rounded-sm p-3 ${
                    isPending ? 'border-amber-300 dark:border-amber-800/40 bg-amber-50/30 dark:bg-amber-950/10' :
                    p.status === 'approved' ? 'border-emerald-200 dark:border-emerald-800/30 bg-emerald-50/20 dark:bg-emerald-950/10' :
                    'border-border bg-card opacity-60'
                  }`} data-testid={`pm-proposal-${p.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">{p.asset_name || 'Asset'}</span>
                        <span className="text-[9px] font-mono text-muted-foreground">by {p.submitted_by}</span>
                        <span className="text-[9px] font-mono text-muted-foreground">{p.trade}</span>
                      </div>
                      <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${
                        p.status === 'approved' ? 'text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800' :
                        p.status === 'rejected' ? 'text-red-700 bg-red-100 border-red-300 dark:text-red-300 dark:bg-red-950/30 dark:border-red-800' :
                        'text-amber-700 bg-amber-100 border-amber-300 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800'
                      }`}>{p.status}{p.reclassified ? ' → Equipment' : ''}</span>
                    </div>

                    {/* Side-by-side diff */}
                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                      <div>
                        <p className="font-heading uppercase tracking-widest text-muted-foreground mb-1">Current</p>
                        {Object.entries(p.current_values || {}).map(([k, v]) => (
                          <div key={k} className="flex gap-1 font-mono py-0.5">
                            <span className="text-muted-foreground w-20">{k}:</span>
                            <span className="text-foreground/70">{typeof v === 'object' ? JSON.stringify(v) : (v || '—')}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <p className="font-heading uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-1">Proposed</p>
                        {Object.entries(p.proposed_changes || {}).map(([k, v]) => (
                          <div key={k} className="flex gap-1 font-mono py-0.5">
                            <span className="text-amber-600 dark:text-amber-400 w-20">{k}:</span>
                            <span className="text-foreground font-medium">{typeof v === 'object' ? JSON.stringify(v) : v}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {p.notes && (
                      <p className="text-[10px] text-muted-foreground mt-1.5 italic">Sub notes: {p.notes}</p>
                    )}

                    {/* Approve/Reject controls */}
                    {isPending && (
                      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/50">
                        <input type="text" value={reviewingId === p.id ? pmNotes : ''} onChange={e => { setReviewingId(p.id); setPmNotes(e.target.value); }}
                          placeholder="Optional notes..."
                          className="flex-1 px-2 py-1.5 text-[10px] bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
                        <button onClick={() => handleReviewProposal(p.id, 'approve')}
                          disabled={reviewingId === p.id && reviewingId !== null}
                          data-testid={`approve-proposal-${p.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm transition-colors disabled:opacity-50">
                          <Check className="w-3 h-3" /> Approve
                        </button>
                        <button onClick={() => handleReviewProposal(p.id, 'reject')}
                          disabled={reviewingId === p.id && reviewingId !== null}
                          data-testid={`reject-proposal-${p.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800/40 rounded-sm hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors disabled:opacity-50">
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    )}

                    {p.pm_notes && p.status !== 'pending' && (
                      <p className="text-[10px] text-muted-foreground mt-1.5 italic">PM notes: {p.pm_notes}</p>
                    )}

                    <p className="text-[9px] text-muted-foreground/60 mt-1">
                      {new Date(p.created_at).toLocaleString()}
                      {p.reviewed_at && ` — reviewed ${new Date(p.reviewed_at).toLocaleString()}`}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* ── Activity Log Section ─────────────────────────────────── */}
      <div className="mt-6 border-t border-border pt-4" data-testid="portal-activity-section">
        <button
          onClick={() => { setShowActivity(!showActivity); if (!showActivity && !activityData) fetchActivity(); }}
          className="flex items-center gap-2 w-full text-left group"
          data-testid="toggle-activity-log-btn"
        >
          <Activity className="w-4 h-4 text-primary/80" />
          <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80">Portal Activity Log</h3>
          {showActivity ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto" />}
        </button>
        <p className="text-[10px] text-muted-foreground mt-0.5 ml-6">
          Track subcontractor portal access and verification events.
        </p>

        {showActivity && (
          <div className="mt-3 space-y-4">
            {activityLoading ? (
              <div className="flex items-center justify-center h-20">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              </div>
            ) : activityData ? (
              <>
                {/* Trade summary cards */}
                {activityData.by_trade?.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="activity-trade-summary">
                    {activityData.by_trade.map(t => (
                      <div key={t.trade} className="px-3 py-2.5 bg-card border border-border rounded-sm" data-testid={`activity-trade-${t.trade}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-foreground">{t.trade}</span>
                          <span className="text-[10px] font-mono text-muted-foreground">{t.total_visits} event{t.total_visits !== 1 ? 's' : ''}</span>
                        </div>
                        {t.email && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{t.email}</p>}
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="inline-flex items-center gap-1 text-[9px] font-mono text-blue-600 dark:text-blue-400">
                            <Eye className="w-3 h-3" /> {t.view_count} view{t.view_count !== 1 ? 's' : ''}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[9px] font-mono text-emerald-600 dark:text-emerald-400">
                            <ShieldCheck className="w-3 h-3" /> {t.verify_count} verified
                          </span>
                        </div>
                        {t.last_access && (
                          <p className="text-[9px] text-muted-foreground/60 mt-1">
                            Last: {new Date(t.last_access).toLocaleDateString()} {new Date(t.last_access).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Recent events table */}
                {activityData.activity?.length > 0 ? (
                  <div className="border border-border rounded-sm overflow-hidden" data-testid="activity-event-table">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-muted/30 border-b border-border">
                          <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Action</th>
                          <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Trade</th>
                          <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Email</th>
                          <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {activityData.activity.map((evt, i) => (
                          <tr key={evt.id || i} className="hover:bg-muted/10" data-testid={`activity-event-${i}`}>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                                evt.action === 'verified'
                                  ? 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800'
                                  : evt.action === 'view_data'
                                  ? 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-950/30 dark:border-blue-800'
                                  : 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800'
                              }`}>
                                {evt.action === 'verified' && <ShieldCheck className="w-3 h-3" />}
                                {evt.action === 'view_data' && <Eye className="w-3 h-3" />}
                                {evt.action === 'verify_attempt' && <Clock className="w-3 h-3" />}
                                {evt.action.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-foreground">{evt.trade}</td>
                            <td className="px-3 py-2 text-[10px] text-muted-foreground font-mono">{evt.email || '—'}</td>
                            <td className="px-3 py-2 text-[10px] text-muted-foreground">
                              {new Date(evt.timestamp).toLocaleDateString()} {new Date(evt.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-20 bg-card border border-dashed border-border rounded-sm" data-testid="no-activity-events">
                    <Activity className="w-5 h-5 text-muted-foreground/40 mb-1" />
                    <p className="text-[10px] text-muted-foreground">No portal activity recorded yet</p>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* ── Messages Section ─────────────────────────────────────── */}
      <div className="mt-6 border-t border-border pt-4" data-testid="portal-messages-section">
        <button
          onClick={() => { setShowMessages(!showMessages); if (!showMessages && messageThreads.length === 0) fetchMessageThreads(); }}
          className="flex items-center gap-2 w-full text-left group"
          data-testid="toggle-messages-btn"
        >
          <MessageSquare className="w-4 h-4 text-blue-500" />
          <h3 className="text-xs font-heading uppercase tracking-widest text-primary/80">Sub Messages</h3>
          {messageThreads.filter(t => t.last_sender_role !== 'pm').length > 0 && (
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-500 text-[9px] font-mono font-bold text-white">
              {messageThreads.filter(t => t.last_sender_role !== 'pm').length}
            </span>
          )}
          {showMessages ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto" />}
        </button>

        {showMessages && (
          <div className="mt-3 space-y-3">
            {messagesLoading ? (
              <div className="flex items-center justify-center h-20">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : messageThreads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-20 bg-card border border-dashed border-border rounded-sm">
                <MessageSquare className="w-5 h-5 text-muted-foreground/40 mb-1" />
                <p className="text-[10px] text-muted-foreground">No messages from subs yet</p>
              </div>
            ) : (
              <>
                {/* Thread list */}
                <div className="space-y-2">
                  {messageThreads.map((t, i) => (
                    <button key={i}
                      onClick={() => { setActiveThread(t); fetchThreadMessages(t.thread_type, t.thread_id); }}
                      data-testid={`msg-thread-${i}`}
                      className={`w-full text-left px-4 py-3 border rounded-sm transition-colors ${
                        activeThread?.thread_id === t.thread_id
                          ? 'border-blue-500/50 bg-blue-500/5'
                          : 'border-border bg-card hover:bg-muted/20'
                      }`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${
                            t.thread_type === 'rfi'
                              ? 'text-amber-600 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800'
                              : 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-950/30 dark:border-blue-800'
                          }`}>{t.thread_type === 'rfi' ? 'RFI' : 'Portal'}</span>
                          {t.trade && <span className="text-[10px] font-medium text-foreground">{t.trade}</span>}
                          {t.item_label && <span className="text-[9px] text-muted-foreground italic">re: {t.item_label}</span>}
                        </div>
                        <span className="text-[9px] text-muted-foreground font-mono">
                          {t.message_count} msg{t.message_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">
                        <span className="font-medium text-foreground/80">{t.last_sender}:</span> {t.last_text}
                      </p>
                      <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                        {t.last_at ? new Date(t.last_at).toLocaleString() : ''}
                      </p>
                    </button>
                  ))}
                </div>

                {/* Active thread conversation */}
                {activeThread && (
                  <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="pm-thread-view">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/20">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        <span className="text-[10px] font-heading uppercase tracking-widest text-foreground">
                          {activeThread.trade} — {activeThread.item_label || 'General'}
                        </span>
                      </div>
                      <button onClick={() => setActiveThread(null)} className="text-[10px] text-muted-foreground hover:text-foreground">Close</button>
                    </div>
                    <div className="max-h-[280px] overflow-y-auto px-4 py-3 space-y-3" data-testid="pm-thread-messages">
                      {threadMessages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender_role === 'pm' ? 'justify-end' : 'justify-start'}`}>
                          <div className="max-w-[80%]">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-[10px] font-medium text-muted-foreground">
                                {msg.sender_name || msg.sender_email}
                              </span>
                              <span className={`text-[9px] px-1 py-0.5 rounded-sm border ${
                                msg.sender_role === 'pm' ? 'text-blue-600 border-blue-200 dark:text-blue-300 dark:border-blue-800' : 'text-emerald-600 border-emerald-200 dark:text-emerald-300 dark:border-emerald-800'
                              }`}>
                                {msg.sender_role === 'pm' ? 'You' : 'Sub'}
                              </span>
                            </div>
                            <div className={`rounded-sm px-3 py-2 text-xs ${
                              msg.sender_role === 'pm'
                                ? 'bg-blue-500/10 border border-blue-500/20 text-foreground'
                                : 'bg-secondary border border-border text-foreground'
                            }`}>
                              <p className="whitespace-pre-wrap">{msg.text}</p>
                            </div>
                            {msg.item_label && (
                              <p className="text-[9px] text-muted-foreground mt-0.5 italic">Re: {msg.item_label}</p>
                            )}
                            <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Reply input */}
                    <div className="flex items-end gap-2 px-4 py-3 border-t border-border">
                      <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                        placeholder="Reply to sub..."
                        rows={1} data-testid="pm-reply-input"
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (threadMessages.length > 0) handleReply(threadMessages[threadMessages.length - 1].id); } }}
                        className="flex-1 px-3 py-2 text-xs bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 resize-none" />
                      <button
                        onClick={() => { if (threadMessages.length > 0) handleReply(threadMessages[threadMessages.length - 1].id); }}
                        disabled={!replyText.trim() || replying}
                        data-testid="pm-reply-send-btn"
                        className="flex items-center gap-1 px-3 py-2 text-[10px] font-heading uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-40 shrink-0">
                        {replying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Reply
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
